import React, { useState } from 'react';
import clsx from 'clsx';
import { humanise } from '../../../utils/format';
import { Segmented, Select } from '../kit';

// Words and small pieces shared by the caseload, the participant file and their modals.

// Funding as stored has several spellings ('ndia', 'NDIS', 'plan_managed', 'self_managed', and older
// ones). The server reads them all the same way (utils/scWorkflow.fundingTypeOf); so does the screen.
export const fundingType = (src) => {
  const v = String(src == null ? '' : src).trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!v) return null;
  if (['ndia', 'ndis', 'agency', 'agency_managed', 'ndia_managed', 'ndis_managed'].includes(v)) return 'ndia';
  if (['plan', 'plan_managed', 'pm', 'plan_manager'].includes(v)) return 'plan';
  if (['self', 'self_managed', 'sm'].includes(v)) return 'self';
  return null;
};
const FUNDING_WORDS = { ndia: 'NDIA-managed', plan: 'Plan-managed', self: 'Self-managed' };
export const fundingLabel = (src) => FUNDING_WORDS[fundingType(src)] || (src ? humanise(src) : 'Funding not set');
// The values the participant form saves, and the stored spelling each type maps back to.
export const FUNDING = { ndia: 'NDIA-managed', plan_managed: 'Plan-managed', self_managed: 'Self-managed' };
export const fundingValue = (src) => ({ ndia: 'ndia', plan: 'plan_managed', self: 'self_managed' }[fundingType(src)] || 'ndia');

export const LEVELS = { support_connection: 'Support connection', support_coordination: 'Support coordination', specialist: 'Specialist support coordination' };
export const SVC_STATUSES = ['sourcing', 'quoted', 'engaged', 'completed', 'cancelled'];
export const CLAIM_KIND = { ndis_csv: 'NDIA claim file', cos_invoice: 'Invoice' };
export const COMM_CHANNEL = { email: 'Email', sms: 'SMS', call: 'Call', meeting: 'Meeting', note: 'Note', portal: 'Portal message' };
export const COMM_DIR = { incoming: 'Incoming', outgoing: 'Outgoing', internal: 'Internal' };
export const REPORT_TYPES = { progress_8week: '8-week progress', mid_plan: 'Mid-plan review', end_of_plan: 'End of plan', crisis: 'Crisis or escalation', other: 'Other' };
// Change of circumstances: a participant's situation changing mid-plan (NDIS Act s48/s47A).
export const COC_ACTION_LABEL = { update_only: 'Update details only', s47a_variation: 'Plan variation (s47A)', s48_reassessment: 'Plan reassessment (s48)', no_action: 'No plan change' };
export const COC_STATUS_LABEL = { draft: 'Draft', in_review: 'In review', submitted: 'Submitted to NDIA', approved: 'Approved', rejected: 'Rejected', implemented: 'Implemented' };
export const COC_CATEGORIES = ['Living situation / housing', 'Informal supports / carer', 'Health / functional capacity', 'Employment / study', 'Contact details', 'Plan management / nominee', 'Risk / safeguarding', 'Funding adequacy', 'Goals', 'Other'];

export const fullName = (first, last) => `${first || ''} ${last || ''}`.trim();

// Whole days from today (Sydney-agnostic, by calendar date) to an ISO date: negative = past.
export const daysFromToday = (d) => {
  if (!d) return null;
  const s = String(d).slice(0, 10);
  const [y, m, dd] = s.split('-').map(Number);
  if (!y || !m || !dd) return null;
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((Date.UTC(y, m - 1, dd) - today) / 86400000);
};
export const agoText = (days) => (days == null ? '' : days <= 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`);
export const inText = (days) => (days == null ? '' : days < 0 ? `${-days} day${days === -1 ? '' : 's'} overdue` : days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`);

// A time entry as the time-entry sheet expects it. Approved, billed, claimed or paid time is locked
// (never edited; corrected with an adjustment), whatever the list said when it loaded.
export const asTimeEntry = (n) => ({
  ...n,
  note_date: String(n.note_date || '').slice(0, 10),
  start_time: n.start_time ? String(n.start_time).slice(0, 5) : '',
  end_time: n.end_time ? String(n.end_time).slice(0, 5) : '',
  // The server decides the lock (claim file or invoice downloaded or sent, or pay marked Paid).
  locked: !!n.locked,
});

// One fact about the participant in the file header. The words carry the meaning; the tint only
// repeats it (violet = Support Coordination's own figure, amber = getting low, red = overspent).
const CHIP = {
  slate: 'bg-white text-slate-700 ring-slate-200',
  violet: 'bg-violet-50 text-violet-800 ring-violet-200/80',
  amber: 'bg-amber-50 text-amber-900 ring-amber-200',
  red: 'bg-rose-50 text-rose-800 ring-rose-200',
};
export function Chip({ icon: Icon, label, children, tone = 'slate' }) {
  return (
    <span className={clsx('inline-flex items-center gap-1.5 max-w-full rounded-full ring-1 ring-inset px-2.5 py-1 text-[12.5px] font-medium', CHIP[tone] || CHIP.slate)}>
      {Icon && <Icon size={13} className="flex-shrink-0 opacity-70" aria-hidden />}
      {label && <span className="sr-only">{label}: </span>}
      <span className="truncate">{children}</span>
    </span>
  );
}

// Views of one list: a Segmented control from 640px up, a labelled picker on phones (four options
// with counts do not fit 390px, and a control that scrolls sideways hides the last view).
let pickerSeq = 0;
export function ViewPicker({ label, value, onChange, options }) {
  const [id] = useState(() => `sc-view-${++pickerSeq}`);
  return (
    <>
      <div className="sm:hidden w-full">
        <label htmlFor={id} className="sr-only">{label}</label>
        <Select id={id} value={value} onChange={onChange}
          options={options.map((o) => ({ value: o.value, label: o.count > 0 ? `${o.label} (${o.count})` : o.label }))} />
      </div>
      <div className="hidden sm:block" role="group" aria-label={label}>
        <Segmented value={value} onChange={onChange} size="sm" options={options} />
      </div>
    </>
  );
}

// A quiet text action inside a panel header ("Add goal", "Edit").
export function LinkButton({ icon: Icon, children, onClick, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[13px] font-semibold text-indigo-700 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50">
      {Icon && <Icon size={14} aria-hidden />}{children}
    </button>
  );
}
